import Sugar from 'sugar';
import { html, css } from 'lit';
import { customElement } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { when } from 'lit/directives/when.js';
import { consume } from '@lit/context';
import { ltnRouterContext } from '../../../ns-services/contexts.js';

import { LtnRouter } from '../../../ltn-router/ltn-router.js';

import NSJobComponent from '../job.js';
import '../../../ns-elements/ns-job-layout.js';
import JobSchema from '../../../models/job-schema.js';
import definition from '@job-schemas/lambda-error-debug.json';
import { jobSchema } from '../job-schema.js';

@customElement(NSLambdaErrorDebug.is)
export default class NSLambdaErrorDebug extends NSJobComponent {
  static get is() {
    return 'ns-lambda-error-debug';
  }

  @consume({ context: ltnRouterContext })
  private __router?: LtnRouter;

  protected _provider?: string;

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { complete: { type: 'button', label: 'Complete' } } });
  }

  static styles = [
    ...super.styles,
    css`
      .label {
        margin-right: var(--wa-space-2xs);
      }

      .job-panel .layout.vertical {
        gap: var(--wa-space-m);
      }
    `,
  ];

  async connectedCallback() {
    await super.connectedCallback();

    if (!this._db) throw new Error('Unable to find db service');

    if (!this.__router) throw new Error('Unable to find LtnRouter service');
  }

  private async __completeJob() {
    this.outcome('complete');
  }

  render() {
    return html`
      <ns-job-layout>
        <div class="job-panel">
          ${this._job
            ? repeat(
                Object.keys(this._job.inputValues),
                (key: string) => html`
                  ${when(
                    Sugar.String.underscore(key).toUpperCase() === 'ERROR',
                    () => html`
                      <div class="layout horizontal">
                        <div class="label">Error:</div>
                        <div class="error-text">${this._job?.inputValues[key]}</div>
                      </div>
                    `,
                  )}
                  ${when(Sugar.String.underscore(key).toUpperCase() === 'EVENT_ID', () => {
                    const val = this._job?.inputValues[key] as string;
                    if (typeof val !== 'string' || val.startsWith('@')) return;
                    return html`
                      <div class="layout horizontal">
                        <div class="label">Event ID:</div>
                        <div class="event-id">${val}</div>
                      </div>
                    `;
                  })}
                  ${when(Sugar.String.underscore(key).toUpperCase() === 'EVENT_TYPE', () => {
                    const val = this._job?.inputValues[key] as string;
                    if (typeof val !== 'string' || val.startsWith('@')) return;
                    return html`
                      <div class="layout horizontal">
                        <div class="label">Event Type:</div>
                        <div class="event-type">${val}</div>
                      </div>
                    `;
                  })}
                  ${when(Sugar.String.underscore(key).toUpperCase() === 'EVENT_OBJECT', () => {
                    if (!this._job || !this._job.inputValues) return;
                    const val = this._job.inputValues[key] as string;
                    if (typeof val !== 'string' || val.startsWith('@')) return;
                    let data: { object?: unknown; id?: unknown } | null;
                    try {
                      data = JSON.parse(val);
                    } catch {
                      data = null;
                    }
                    if (!data || typeof data !== 'object') {
                      return html`
                        <div class="layout horizontal">
                          <div class="label">Object:</div>
                          <div class="object">${val}</div>
                        </div>
                      `;
                    }
                    return html`
                      <div class="layout vertical">
                        <div class="layout horizontal">
                          <div class="label">Object:</div>
                          <div class="object">${data.object}</div>
                        </div>
                        <div class="layout horizontal">
                          <div class="label">Object ID:</div>
                          <div class="object-id">${data.id}</div>
                        </div>
                      </div>
                    `;
                  })}
                `,
              )
            : undefined}
        </div>
      </ns-job-layout>
    `;
  }
}
